// src/components/PlaysList.js
import React, { useCallback, useEffect, useState } from "react";
import ClipLoader from "react-spinners/ClipLoader";
import { apiFetch } from "../api";

const PAGE_SIZE = 10;

export default function PlaysList({ ruleset = "high_school" }) {
  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");
  const [reviewFilter, setReviewFilter] = useState("");
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);

  // Drawer state
  const [openRulesForId, setOpenRulesForId] = useState(null);
  const [drawerRules, setDrawerRules] = useState([]);

  // Review modal state
  const [reviewForId, setReviewForId] = useState(null);
  const [reviewPrediction, setReviewPrediction] = useState("");
  const [reviewLabel, setReviewLabel] = useState("");
  const [reviewNotes, setReviewNotes] = useState("");
  const [feedbackSavingId, setFeedbackSavingId] = useState(null);

  const fetchPlays = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
        offset: String(page * PAGE_SIZE),
        ruleset,
      });
      if (statusFilter) params.set("status", statusFilter);
      if (reviewFilter) params.set("reviewed", reviewFilter);
      if (searchText.trim()) params.set("q", searchText.trim());

      const res = await apiFetch(`/plays?${params.toString()}`);
      const data = await res.json();
      if (Array.isArray(data)) {
        setRows(data);
        setTotalRows(data.length);
      } else {
        setRows(Array.isArray(data.items) ? data.items : []);
        setTotalRows(Number.isFinite(data.total) ? data.total : 0);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, reviewFilter, searchText, ruleset]);

useEffect(() => {
  fetchPlays(); // initial load
  const interval = setInterval(fetchPlays, 3000); // poll every 3 seconds
  return () => clearInterval(interval); // cleanup on unmount
}, [fetchPlays]);

  function resetToFirstPage(setter) {
    setter();
    setPage(0);
  }

  const firstItem = totalRows === 0 ? 0 : page * PAGE_SIZE + 1;
  const lastItem = Math.min((page + 1) * PAGE_SIZE, totalRows);
  const hasPreviousPage = page > 0;
  const hasNextPage = lastItem < totalRows;

  async function onRetry(id) {
    try {
      await apiFetch(`/retry/${id}`, {
        method: "POST",
      });
      await fetchPlays();
    } catch (e) {
      console.error(e);
    }
  }

  // ---- Rules Drawer ----
  function openRulesDrawer(row) {
    if (Array.isArray(row.retrieved_rules) && row.retrieved_rules.length) {
      setDrawerRules(row.retrieved_rules);
      setOpenRulesForId(row.id);
      return;
    }
    // Fallback: query using hint & notes
    const q = `${row.foul_type || ""} ${row.notes || ""}`.trim() || "football rules";
    const params = new URLSearchParams({ q, k: "3", ruleset });
    apiFetch(`/rules/search?${params.toString()}`)
      .then(r => r.json())
      .then(data => {
        setDrawerRules(Array.isArray(data) ? data : []);
        setOpenRulesForId(row.id);
      })
      .catch(err => {
        console.error(err);
        setDrawerRules([]);
        setOpenRulesForId(row.id);
      });
  }

  function closeRules() {
    setOpenRulesForId(null);
    setDrawerRules([]);
  }

  // ---- Human Review Modal ----
  function openReview(row) {
    setReviewForId(row.id);
    setReviewPrediction(row.prediction_label || "");
    setReviewLabel(row.human_label && row.human_label !== row.prediction_label ? row.human_label : "");
    setReviewNotes(row.human_notes || "");
  }

  function closeReview() {
    setReviewForId(null);
    setReviewPrediction("");
    setReviewLabel("");
    setReviewNotes("");
  }

  async function submitAgreement(row) {
    if (!row.prediction_label) {
      alert("No AI prediction is available to agree with.");
      return;
    }
    setFeedbackSavingId(row.id);
    try {
      const res = await apiFetch(`/review/${row.id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          human_label: row.prediction_label,
          human_notes: "",
          override_prediction: false,
        }),
      });
      const data = await res.json();
      if (!data.ok) {
        alert(`Error: ${data.error || "failed"}`);
      } else {
        await fetchPlays();
      }
    } catch (e) {
      alert(`Error: ${String(e)}`);
    } finally {
      setFeedbackSavingId(null);
    }
  }

  async function submitReview() {
    if (!reviewForId || !reviewLabel.trim()) {
      alert("Please enter the correct ruling.");
      return;
    }
    try {
      const res = await apiFetch(`/review/${reviewForId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          human_label: reviewLabel.trim(),
          human_notes: reviewNotes,
          override_prediction: false,
        }),
      });
      const data = await res.json();
      if (!data.ok) {
        alert(`Error: ${data.error || "failed"}`);
      } else {
        await fetchPlays();
        closeReview();
      }
    } catch (e) {
      alert(`Error: ${String(e)}`);
    }
  }

  return (
    <section className="listPanel">
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <button onClick={fetchPlays} disabled={loading}>
          Refresh
        </button>
        {loading && <ClipLoader size={20} color="#333" />}
      </div>

      <div style={drawerStyle.filters}>
        <input
          value={searchText}
          onChange={(e) => resetToFirstPage(() => setSearchText(e.target.value))}
          placeholder="Search plays"
          style={drawerStyle.filterInput}
        />
        <select
          value={statusFilter}
          onChange={(e) => resetToFirstPage(() => setStatusFilter(e.target.value))}
        >
          <option value="">All statuses</option>
          <option value="queued">Queued</option>
          <option value="processing">Processing</option>
          <option value="done">Done</option>
          <option value="error">Error</option>
        </select>
        <select
          value={reviewFilter}
          onChange={(e) => resetToFirstPage(() => setReviewFilter(e.target.value))}
        >
          <option value="">All reviews</option>
          <option value="false">Needs review</option>
          <option value="true">Reviewed</option>
        </select>
      </div>

      <table style={{ width: "100%", marginTop: 12, borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th align="left">ID</th>
            <th align="left">Foul Hint</th>
            <th align="left">Status</th>
            <th align="left">Prediction</th>
            <th align="left">Conf.</th>
            <th align="left">Processed</th>
            <th align="left">Notes</th>
            <th align="left">Watch</th>
            <th align="left">Explanation</th>
            <th align="left">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} style={{ borderTop: "1px solid #eee" }}>
              <td>{r.id}</td>
              <td>{r.foul_type || "—"}</td>
              <td>{r.status}</td>
              <td>
                {r.prediction_label || "—"}
                {r.reviewed_at ? (
                  <span title="Official feedback" style={{ marginLeft: 6, fontSize: 12, color: "#666" }}>
                    {r.official_agrees === true || (r.human_label && r.human_label === r.prediction_label)
                      ? "(official agreed)"
                      : r.human_label
                        ? `(official: ${r.human_label})`
                        : "(reviewed)"}
                  </span>
                ) : null}
              </td>
              <td>{r.confidence != null ? r.confidence.toFixed(2) : "—"}</td>
              <td>{r.processed_at ? new Date(r.processed_at).toLocaleString() : "—"}</td>
              <td>{r.notes || "—"}</td>
              <td>
                {r.presigned_url ? (
                  <a href={r.presigned_url} target="_blank" rel="noreferrer">Open</a>
                ) : "—"}
              </td>
              <td style={{ maxWidth: 420 }}>
                {r.explanation ? (
                  <span title={r.explanation}>
                    {r.explanation.length > 120 ? r.explanation.slice(0, 120) + "…" : r.explanation}
                  </span>
                ) : "—"}
              </td>
              <td>
                <div style={{ display: "flex", gap: 6 }}>
                  <button onClick={() => onRetry(r.id)}>Retry</button>
                  <button onClick={() => openRulesDrawer(r)}>Rules</button>
                  <button
                    onClick={() => submitAgreement(r)}
                    disabled={feedbackSavingId === r.id || !r.prediction_label}
                  >
                    Agree
                  </button>
                  <button onClick={() => openReview(r)}>Disagree</button>
                </div>
              </td>
            </tr>
          ))}
          {!rows.length && (
            <tr><td colSpan={10} style={{ padding: 16, color: "#777" }}>No items.</td></tr>
          )}
        </tbody>
      </table>

      <div style={drawerStyle.pagination}>
        <span>
          {totalRows ? `${firstItem}-${lastItem} of ${totalRows}` : "0 plays"}
        </span>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={() => setPage((current) => Math.max(current - 1, 0))}
            disabled={!hasPreviousPage || loading}
          >
            Previous
          </button>
          <button
            onClick={() => setPage((current) => current + 1)}
            disabled={!hasNextPage || loading}
          >
            Next
          </button>
        </div>
      </div>

      {/* Rules Drawer */}
      {openRulesForId !== null && (
        <div style={drawerStyle.backdrop} onClick={closeRules}>
          <div style={drawerStyle.panel} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h3 style={{ margin: 0 }}>Retrieved Rules</h3>
              <button onClick={closeRules}>Close</button>
            </div>
            <div style={{ marginTop: 12, display: "grid", gap: 12 }}>
              {drawerRules.length ? drawerRules.map((rr, i) => (
                <div key={i} style={drawerStyle.card}>
                  <div style={{ fontWeight: 600 }}>
                    {rr.title}{rr.section ? ` — ${rr.section}` : ""}
                  </div>
                  <div style={{ fontSize: 12, opacity: 0.7 }}>
                    Score: {rr.score != null ? rr.score.toFixed(3) : "—"}
                  </div>
                  <div style={{ marginTop: 6, whiteSpace: "pre-wrap" }}>
                    {rr.body || ""}
                  </div>
                </div>
              )) : <div style={{ color: "#777" }}>No rules found.</div>}
            </div>
          </div>
        </div>
      )}

      {/* Review Modal */}
      {reviewForId !== null && (
        <div style={drawerStyle.backdrop} onClick={closeReview}>
          <div style={drawerStyle.modal} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>Correct Official Ruling</h3>
            <div style={{ display: "grid", gap: 10 }}>
              {reviewPrediction ? (
                <div style={drawerStyle.predictionSummary}>
                  AI prediction: <strong>{reviewPrediction}</strong>
                </div>
              ) : null}
              <input
                value={reviewLabel}
                onChange={(e) => setReviewLabel(e.target.value)}
                placeholder="Correct ruling"
              />
              <textarea
                rows={4}
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                placeholder="Notes (optional)"
              />
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button onClick={closeReview}>Cancel</button>
                <button onClick={submitReview}>Save correction</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

const drawerStyle = {
  backdrop: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.25)",
    display: "flex", justifyContent: "flex-end", zIndex: 50
  },
  panel: {
    width: "min(480px, 95vw)", height: "100%", background: "#fff",
    padding: 16, boxShadow: "0 0 24px rgba(0,0,0,0.2)"
  },
  modal: {
    margin: "auto", width: "min(520px, 96vw)", background: "#fff",
    padding: 16, borderRadius: 8, boxShadow: "0 0 24px rgba(0,0,0,0.25)"
  },
  filters: {
    display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12, alignItems: "center"
  },
  filterInput: {
    minWidth: 180
  },
  pagination: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    gap: 12, marginTop: 12, color: "#555"
  },
  predictionSummary: {
    border: "1px solid #eee", borderRadius: 8, padding: 10, background: "#fafafa"
  },
  card: {
    border: "1px solid #eee", borderRadius: 8, padding: 12, background: "#fafafa"
  }
};
